import { BudgetActionTypes } from '@bridebook/web/lib/budget/action-types';
import { getEpic } from '@bridebook/web/lib/configure-epics';
import { triggerEpicsAdded } from '@bridebook/web/lib/epics/actions';
import type { IEpic } from '@bridebook/web/lib/types';
import { useDispatch, useSelector, useStore } from '@bridebook/web/lib/utils';
import { useEffect, useState } from 'react';

/**
 * Fills `state.budget` for the planner. The budget page and home widgets start the budget
 * listener, and its epics are route-lazy, so on other routes every budget answer would be "no
 * budget data yet". The epics and listener are shared and owned by a boolean, not a refcount,
 * so this never stops them, except on sign-out, where nobody can need them. The epics are
 * imported dynamically to keep the budget domain out of the global page bundle. Reports
 * whether that import failed: then the listener never starts and the slice stays empty.
 */
export const useBudgetSlice = (weddingId: string | undefined): { failed: boolean } => {
  const dispatch = useDispatch();
  const store = useStore();
  const epicsAdded = useSelector((state) => state.epics.epicsAdded.budget);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!weddingId || epicsAdded) {
      return;
    }
    void import('@bridebook/web/lib/budget/epics')
      .then(({ pageEpics }) => {
        const rootEpic = getEpic();
        // The budget route can inject them while the import is in flight, and a second
        // injection runs every budget epic twice, so the guard is re-read from the store.
        if (!rootEpic || store.getState().epics.epicsAdded.budget) {
          return;
        }
        pageEpics.forEach((epic) => rootEpic.next(epic as IEpic));
        dispatch(triggerEpicsAdded('budget'));
      })
      .catch(() => setFailed(true));
  }, [dispatch, store, weddingId, epicsAdded]);

  // Starting the listener before its epic exists would be a no-op, so it waits.
  useEffect(() => {
    if (!weddingId || !epicsAdded) {
      return;
    }
    dispatch({ type: BudgetActionTypes.INIT_LISTENER });
    // This also runs on unmount with a couple still signed in, where stopping
    // would break whoever else is listening — so it stops only once the wedding
    // is gone, which is a sign-out and leaves the subscription unowned.
    return () => {
      if (!store.getState().weddings.profile.id) {
        dispatch({ type: BudgetActionTypes.STOP_LISTENER });
      }
    };
  }, [dispatch, store, weddingId, epicsAdded]);

  return { failed };
};
